import { api, type Page } from './client'
import { guardedRequest, type GuardedResult } from './pipelines'
import type { Contest, ContestDetail } from './performance'

/**
 * TASK-0183 (REQ-021-002) — `scope` type augmentation for `Contest`/`ContestDetail`.
 *
 * DEVIATION from the task card, disclosed here: the card says to add `scope?: string | null` "on
 * the pertinent interfaces" in THIS file, on the assumption that `Contest`/`ContestDetail` are
 * defined here. They are not — both live in `api/performance.ts` (`contestsApi.list/get/create/
 * update/updateStatus/delete`, confirmed by reading that file in full), which is NOT in this task's
 * `allowed_paths`/`forbidden_paths` list (only `api/contests.ts` and `pages/ContestsPage.tsx` are
 * listed for the frontend). Editing `performance.ts` would violate the "only touch allowed_paths"
 * constraint, so instead of modifying the base interfaces, `ContestWithScope`/`ContestDetailWithScope`
 * EXTEND them here with the one new optional field, and `ContestsPage.tsx` (also allowed) uses these
 * extended types in place of the bare `Contest`/`ContestDetail` it previously imported. This is
 * type-safe and requires no runtime change: a `Contest` value returned by `contestsApi.list`/`create`/
 * `update` (which don't know about `scope` in their TS types) is structurally still a valid
 * `ContestWithScope` because the added field is optional — no cast needed anywhere this is used.
 */
export interface ContestWithScope extends Contest {
  scope?: string | null
  // TASK-0193: same extension technique as `scope` right above, same reason (base interface lives in
  // `api/performance.ts`, out of this task's allowed_paths) — no new interface, `sector` just joins
  // `scope` on these same two existing ones.
  sector?: string | null
  // TASK-0250 (REQ-021-001): same extension technique as `scope`/`sector` right above, same reason
  // (the base interface lives in `api/performance.ts`, outside this task's allowed paths) —
  // `customRules` just joins them on these same two existing interfaces. Free prose the admin typed,
  // exposed unconditionally by `ContestsController.List`/`Get`; nothing on the frontend interprets
  // it either (see `Contest.CustomRules`'s own remarks — the field is capture-and-display only).
  customRules?: string | null
}

export interface ContestDetailWithScope extends ContestDetail {
  scope?: string | null
  sector?: string | null
  customRules?: string | null
}

/**
 * TASK-0038: participant-management calls for `ContestDetailPage.tsx`. Kept in this dedicated file
 * (rather than added to `performance.ts`, which already owns `contestsApi.list`/`get`/`create`/
 * `update`/`updateStatus`/`delete` and `Contest`/`ContestDetail`) because this task's allowed paths
 * name `api/contests.ts` specifically and `performance.ts` is out of scope — `ContestDetailPage.tsx`
 * imports `contestsApi`/`ContestDetail` from `./performance` for reads and these two calls from here
 * for the two new Admin-only writes.
 */
export const contestParticipantsApi = {
  add: (contestId: number, userId: number) =>
    api<{ id: number }>(`/api/contests/${contestId}/participants`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  remove: (contestId: number, participantId: number) =>
    api<void>(`/api/contests/${contestId}/participants/${participantId}`, { method: 'DELETE' }),
}

/**
 * F-101 (REQ-277-001): elenco gare con ordinamento lato server. `sort` = default (attive per prime, poi fine
 * crescente) | start_desc | end_asc | name. Sta qui e non in `contestsApi.list` (`api/performance.ts`) perche'
 * quel file non e' di questa card; gli altri chiamanti di `contestsApi.list` restano com'erano.
 */
export function listContestsSorted(p: { page?: number; status?: string; sort?: string }) {
  const params = new URLSearchParams()
  if (p.page) params.set('page', String(p.page))
  if (p.status) params.set('status', p.status)
  if (p.sort && p.sort !== 'default') params.set('sort', p.sort)
  return api<Page<ContestWithScope>>(`/api/contests?${params}`)
}

/**
 * F-142 (REQ-293-001): annullamento / archiviazione di una gara con la guardia degli usi. Con la guardia accesa e usi
 * bloccanti (partecipanti, spese, premi assegnati...) il server risponde 409 `{ usages }`: si chiede conferma e si
 * ripete con `confirm=true`. Con la guardia spenta la risposta e' quella di sempre. `archived` e' vero quando la gara e'
 * stata annullata invece che rimossa.
 */
export type ContestArchiveResult = GuardedResult & { archived?: boolean }

export async function deleteContestGuarded(id: number, confirm: boolean): Promise<ContestArchiveResult> {
  let archived = false
  const res = await guardedRequest(async () => {
    const r = await api<{ archived?: boolean } | undefined>(`/api/contests/${id}${confirm ? '?confirm=true' : ''}`, { method: 'DELETE' })
    archived = r?.archived === true
  })
  return res.status === 'done' ? { ...res, archived } : res
}

export async function cancelContestGuarded(id: number, confirm: boolean): Promise<GuardedResult> {
  return guardedRequest(() => api<void>(`/api/contests/${id}/status${confirm ? '?confirm=true' : ''}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'cancelled' }),
  }))
}

/** F-142 (REQ-028-007): premio di una gara. Nessun importo. */
export type ContestAwardStatus = 'Previsto' | 'Assegnato' | 'Consegnato' | 'Annullato'

export interface ContestAwardAssignment {
  id: number
  userId: number
  assignedBy: number | null
  assignedAt: string
  deliveredAt: string | null
  note: string | null
}

export interface ContestAward {
  id: number
  contestId: number
  positionFrom: number
  positionTo: number
  name: string
  description: string | null
  status: ContestAwardStatus
  createdAt: string
  assignments: ContestAwardAssignment[]
}

export interface SuggestedWinner {
  userId: number
  rank: number
  alreadyAssigned: boolean
}

export const contestAwardsApi = {
  /** Lo staff vede tutti i premi; un agente solo i propri (e solo la propria assegnazione). */
  list: (contestId: number) => api<ContestAward[]>(`/api/contests/${contestId}/awards`),
  create: (contestId: number, body: { positionFrom: number; positionTo?: number; name: string; description?: string }) =>
    api<ContestAward>(`/api/contests/${contestId}/awards`, { method: 'POST', body: JSON.stringify(body) }),
  remove: (contestId: number, awardId: number) =>
    api<{ archived?: boolean } | undefined>(`/api/contests/${contestId}/awards/${awardId}`, { method: 'DELETE' }),
  /** Senza `userId` non assegna: restituisce solo i vincitori proposti dalla classifica. */
  assign: (contestId: number, awardId: number, body: { userId?: number; note?: string }) =>
    api<{ award: ContestAward; suggestedWinners: SuggestedWinner[] }>(
      `/api/contests/${contestId}/awards/${awardId}/assign`, { method: 'POST', body: JSON.stringify(body) }),
  deliver: (contestId: number, awardId: number, body: { userId?: number; note?: string }) =>
    api<ContestAward>(`/api/contests/${contestId}/awards/${awardId}/deliver`, { method: 'POST', body: JSON.stringify(body) }),
}
